import {
  each,
  find,
  isDate,
  isNumber,
  isString,
  map,
  reduce,
  size,
  some,
} from "lodash";
import moment, { Moment, unitOfTime } from "moment-timezone";
import { getValidLocale } from "@backpacker/intl";
import { AbstractDate, Interval, Range } from "@linkfluence/models/date";
import { Locale } from "@linkfluence/models/locale";
import { Timezone } from "@linkfluence/models/timezone";
import { useFeature } from "@radarly/core/features/use-cases";

type DateMathInterval = "d" | "M" | "y" | "q" | "h" | "w";

type DateMathOperation = {
  unit: DateMathInterval;
  num: number;
  type: number;
};

type Duration = unitOfTime.Base | unitOfTime._quarter;

const birthDateRegexp =
  /([12]\d{3}(-|\/|\s)(0[1-9]|1[0-2])(-|\/|\s)(0[1-9]|[12]\d|3[01]))/;

function clone(date: AbstractDate) {
  return mmt(date).clone();
}

function subtract(date: AbstractDate, number: number, duration: Duration) {
  return clone(date).subtract(number, duration);
}

function add(date: AbstractDate, number: number, duration: Duration) {
  return clone(date).add(number, duration);
}

function getIsoInterval(interval: unitOfTime.StartOf) {
  return interval === Interval.Week ? "isoWeek" : interval;
}

export function getDatesFromRange(
  range: Range,
  includeEndOfRange?: boolean
): Moment[] {
  const dateArray: Moment[] = [];
  const currentDate = mmt(range.from);
  const stopDate = mmt(range.to);

  const nextDate = currentDate.clone();
  while (nextDate <= stopDate) {
    dateArray.push(nextDate.clone());
    nextDate.add(1, range.interval);
  }
  if (includeEndOfRange) {
    dateArray.push(stopDate);
  }
  return dateArray;
}

export function mmt(date: AbstractDate) {
  if (isString(date) || isDate(date) || isNumber(date)) {
    return moment(date);
  } else if (moment.isMoment(date)) {
    return date;
  } else {
    return now("UTC");
  }
}

export function getBirthdate(age: number, timezone: Timezone = "UTC") {
  return subtract(now(timezone), age, Interval.Year);
}

export function getHours() {
  const hours: { value: number; label: string }[] = [];
  for (let i = 0; i < 24; i++) {
    hours.push({
      value: i,
      label: i + "h",
    });
  }
  return hours;
}

export function getMinutes() {
  const minutes: { value: number; label: string }[] = [];
  for (let i = 0; i < 12; i++) {
    minutes.push({
      value: i * 5,
      label: (i < 2 ? "0" : "") + (i * 5).toString(),
    });
  }
  return minutes;
}

export function startOf(
  date: AbstractDate,
  interval: unitOfTime.StartOf,
  iso = true
) {
  return clone(date).startOf(iso ? getIsoInterval(interval) : interval);
}

export function endOf(
  date: AbstractDate,
  interval: unitOfTime.StartOf,
  iso = true
) {
  return clone(date).endOf(iso ? getIsoInterval(interval) : interval);
}

export function startOfDay(date: AbstractDate) {
  return startOf(date, Interval.Day);
}

export function startOfMonth(date: AbstractDate) {
  return startOf(date, Interval.Month);
}

export function startOfYear(date: AbstractDate) {
  return startOf(date, Interval.Year);
}

export function endOfDay(date: AbstractDate) {
  return endOf(date, Interval.Day);
}

export function endOfMonth(date: AbstractDate) {
  return endOf(date, Interval.Month);
}

export function endOfYear(date: AbstractDate) {
  return endOf(date, Interval.Year);
}

export function isSame(
  a: AbstractDate,
  b: AbstractDate,
  granularity?: unitOfTime.StartOf
) {
  return mmt(a).isSame(mmt(b), granularity);
}

export function getTimezones() {
  return moment.tz.names();
}

export function isBefore(a: AbstractDate, b: AbstractDate) {
  return mmt(a).isBefore(mmt(b));
}

export function isAfter(a: AbstractDate, b: AbstractDate) {
  return mmt(a).isAfter(mmt(b));
}

export function isBetween(
  date: AbstractDate,
  from: AbstractDate,
  to: AbstractDate,
  exclusive?: boolean
) {
  if (exclusive) {
    return mmt(date).isBetween(mmt(from), mmt(to));
  } else {
    return (
      mmt(date).isSameOrBefore(mmt(to)) && mmt(date).isSameOrAfter(mmt(from))
    );
  }
}

export function isMoment(date: AbstractDate | object): date is moment.Moment {
  return moment.isMoment(date);
}

export function isValidBirthDate(date: string) {
  return !!size(date.match(birthDateRegexp));
}

export function diff(
  a: AbstractDate,
  b: AbstractDate,
  interval: Interval,
  includeLastDate: boolean = false
) {
  if (a && b) {
    const startInterval = interval !== Interval.Hour ? Interval.Day : interval;
    return (
      clone(startOf(b, startInterval)).diff(
        clone(startOf(a, startInterval)),
        interval
      ) + (includeLastDate ? 1 : 0)
    );
  } else {
    return 0;
  }
}

// @TODO deprecated
export function previousRange({ from, to, interval }: Range) {
  return {
    from: previous(previous(from, diff(from, to, interval))),
    to: previous(from),
    interval,
  };
}

export function getLastYearPeriod({
  from,
  to,
}: {
  from: AbstractDate;
  to: AbstractDate;
}) {
  function getPreviousLastYearOperations(date: string) {
    const operations = extractOperations(date);
    operations.unshift({
      unit: "y",
      type: 2,
      num: 1,
    });
    return operations;
  }

  function getPreviousByDateType(date: AbstractDate) {
    return isDateMath(date)
      ? fromOperationsToDateMath(getPreviousLastYearOperations(date))
      : previous(date, 1, Interval.Year).toISOString();
  }

  return {
    from: getPreviousByDateType(from),
    to: getPreviousByDateType(to),
  };
}

export function getPreviousPeriod({
  from,
  to,
  interval,
}: {
  from: AbstractDate;
  to: AbstractDate;
  interval?: Interval;
}) {
  if (isDateMath(from) && isDateMath(to)) {
    function getInterval(date: string) {
      const operations = extractOperations(date);
      const roundedOperation = find(operations, { type: 0 });
      return roundedOperation ? roundedOperation.unit : "d";
    }
    function getIntervalNumber(date: string) {
      const operations = extractOperations(date);
      const roundedOperation = find(operations, { type: 2 });
      return roundedOperation ? roundedOperation.num : 0;
    }
    function getIntervalDiff(from: string, to: string) {
      const diff = getIntervalNumber(from) - getIntervalNumber(to);
      return diff > 1 ? diff + 1 : diff;
    }
    function getPreviousOperations(
      date: string,
      interval: DateMathInterval,
      diff: number
    ) {
      const operations = extractOperations(date);
      if (!some(operations, { type: 2 })) {
        operations.unshift({
          unit: interval,
          type: 2,
          num: diff === 0 ? 1 : diff,
        });
        return operations;
      } else {
        return map(operations, ({ type, num, unit }) => ({
          unit: type === 2 ? interval : unit,
          type,
          num: type === 2 ? num + diff : num,
        }));
      }
    }
    const interval = getInterval(from);
    const diff = getIntervalDiff(from, to);
    return {
      from: fromOperationsToDateMath(
        getPreviousOperations(from, interval, diff)
      ),
      to: fromOperationsToDateMath(getPreviousOperations(to, interval, diff)),
    };
  } else {
    const fromDate = getDateFromDateMath(from, "UTC");
    const toDate = getDateFromDateMath(to, "UTC", true);
    const _interval = interval ?? getRangeInterval(fromDate, toDate);
    return {
      from: previous(
        fromDate,
        diff(fromDate, toDate, _interval),
        _interval
      ).toISOString(),
      to: mmt(fromDate).toISOString(),
    };
  }
}

export function getLastDaysRange(
  from: AbstractDate,
  to: AbstractDate,
  nbDays: number,
  inclusion?: boolean
) {
  return {
    from: previous(to, Math.min(nbDays, diff(from, to, Interval.Day))),
    to: inclusion ? to : previous(to, 1),
  };
}

export function getMaxRange(
  interval: Interval,
  max?: Partial<Record<Interval, number>>
) {
  const maxRange: Partial<Record<Interval, number>> = {
    [Interval.Hour]: 96,
    [Interval.Day]: 100,
    [Interval.Week]: 100,
    [Interval.Month]: 100,
    [Interval.Year]: 100,
  };
  if (max && max[interval]) {
    return max[interval] || 100;
  }
  return maxRange[interval] || 100;
}

export const duration = moment.duration;

export function getRangeSize(range: Range) {
  return diff(range.from, range.to, range.interval);
}

export function isRangeOutsideMaxRange(
  range: Range,
  max?: Partial<Record<Interval, number>>
) {
  return getRangeSize(range) > getMaxRange(range.interval, max);
}

export function getLimitFromRange(
  range: Range,
  interval: Interval,
  limit: Omit<Range, "interval">
) {
  const maxRange = getMaxRange(interval);
  const rangeSize = diff(range.to, range.from, interval);
  const restCount = Math.ceil((maxRange - 1 - rangeSize) / 2);
  const to = min(
    add(range.to, restCount, interval),
    startOf(endOf(limit.to, interval), interval)
  );
  const from = startOf(
    max(subtract(to, maxRange - 1, interval), startOf(limit.from, interval)),
    interval
  );
  return {
    from,
    to: endOf(
      min(add(from, maxRange - 1, interval), endOf(limit.to, interval)),
      interval
    ),
  };
}

export function truncateRangeLeft(
  { from, to, interval }: Range,
  max?: Partial<Record<Interval, number>>
) {
  const maxRange = getMaxRange(interval, max);
  return {
    from: startOf(from, interval),
    to: endOf(
      diff(to, from, interval) > maxRange - 1
        ? add(from, maxRange - 1, interval)
        : to,
      interval
    ),
    interval,
  };
}

export function truncateRangeRight(
  { from, to, interval }: Range,
  max?: Partial<Record<Interval, number>>
) {
  const maxRange = getMaxRange(interval, max);
  return {
    from: startOf(
      diff(from, to, interval) > maxRange - 1
        ? subtract(to, maxRange - 1, interval)
        : from,
      interval
    ),
    to: endOf(to, interval),
    interval,
  };
}

export function min(a: AbstractDate, b: AbstractDate) {
  return moment.min(mmt(a), mmt(b));
}

export function max(a: AbstractDate, b: AbstractDate) {
  return moment.max(mmt(a), mmt(b));
}

export function previous(
  d: AbstractDate,
  cpt: number = 1,
  duration: Duration = Interval.Day
) {
  return subtract(d, cpt, duration);
}

export function next(
  d: AbstractDate,
  cpt: number = 1,
  duration: Duration = Interval.Day
) {
  return add(d, cpt, duration);
}

export function tz(d: AbstractDate, timezone: Timezone) {
  return moment(d).tz(timezone);
}

export function now(timezone: Timezone) {
  return moment().tz(timezone);
}

export function monthsShort() {
  return moment.monthsShort();
}

export function months() {
  return moment.months();
}

export function weekdaysMin() {
  const _weekdaysMin = moment.weekdaysMin();
  _weekdaysMin.push("" + _weekdaysMin.shift());
  return _weekdaysMin;
}

export function weekdays() {
  const _weekdays = moment.weekdays();
  _weekdays.push("" + _weekdays.shift());
  return _weekdays;
}

export function week(date: AbstractDate) {
  const week = moment(date).isoWeek();
  return week;
}

export function weekYear(date: AbstractDate) {
  const year = moment(date).isoWeekYear();
  return year;
}

export function lowerRangeIsTheLowerBound(
  from: AbstractDate,
  limit: AbstractDate,
  globalLimits: AbstractDate,
  interval: Interval
) {
  return from === 0 && isSame(limit, globalLimits, interval);
}

export function upperRangeIsTheUpperBound(
  to: AbstractDate,
  count: number,
  limit: AbstractDate,
  globalLimits: AbstractDate,
  interval: Interval
) {
  return to === count && isSame(limit, globalLimits, interval);
}

export function getPreviousNumberFromLowerAndUpperBound(
  start: number,
  end: number,
  limit: AbstractDate,
  globalLimits: AbstractDate,
  interval: Interval
) {
  let unitsToSubtract = end - start;
  const limitFrom = diff(startOf(globalLimits, interval), limit, interval);
  const newLimitRange = limitFrom + start - unitsToSubtract;
  if (newLimitRange < 0) {
    unitsToSubtract = unitsToSubtract + newLimitRange;
  }
  return unitsToSubtract;
}

export function getNextNumberFromLowerAndUpperBound(
  start: number,
  end: number,
  count: number,
  limit: AbstractDate,
  globalLimits: AbstractDate,
  interval: Interval
) {
  const unitsToAdd = end - start;
  return Math.max(
    0,
    Math.min(
      Math.max(0, end + unitsToAdd - count),
      diff(limit, endOf(globalLimits, interval), interval)
    ) + Math.min(count - end, unitsToAdd)
  );
}

export function hasValidEvolution(range: Range, limit: Range) {
  const nbDays = diff(range.from, range.to, range.interval);
  const start = diff(limit.from, range.from, limit.interval);
  return nbDays <= start;
}

export function getRangeFromInterval(
  range: Range | Omit<Range, "interval">,
  interval: Interval,
  limit: Range | Omit<Range, "interval">,
  truncate: boolean = false
) {
  const from = max(
    startOf(range.from, interval),
    startOf(limit.from, interval)
  );
  const to = min(endOf(range.to, interval), endOf(limit.to, interval));
  if (truncate) {
    return truncateRangeRight({ from, to, interval });
  } else {
    return {
      from,
      to,
      interval,
    };
  }
}

export function getDateFromDateMath(
  date: AbstractDate,
  timezone: Timezone = "UTC",
  roundUp: boolean = false,
  nowDate?: AbstractDate
) {
  if (!isDateMath(date)) {
    return mmt(date).tz(timezone);
  } else {
    return fromOperationsToDate(
      extractOperations(date),
      timezone,
      roundUp,
      nowDate
    );
  }
}

export const getNativeFormattedDate = (
  date: AbstractDate,
  locale: Locale,
  options?: Intl.DateTimeFormatOptions,
  timezone: Timezone = "UTC",
  roundUp: boolean = false,
  nowDate?: AbstractDate
): string => {
  const mmtDate = getDateFromDateMath(date, timezone, roundUp, nowDate);
  return new Intl.DateTimeFormat(getValidLocale(locale), options).format(
    mmtDate.toDate()
  );
};

export function getIntervalFromDates(
  from: AbstractDate,
  to: AbstractDate,
  timezone: Timezone = "UTC"
) {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const isHourTimeframeUpdateEnabled = useFeature("hour_timeframe_update");

  return getRangeFromDates(
    from,
    to,
    timezone,
    undefined,
    undefined,
    undefined,
    isHourTimeframeUpdateEnabled
  ).interval;
}

export function setNewTimezone(
  date: AbstractDate,
  previousTimezone: Timezone,
  timezone: Timezone
) {
  if (isDateMath(date)) {
    return date;
  } else {
    return mmt(date).tz(previousTimezone).tz(timezone, true).toISOString();
  }
}

export function getRangeFromDates(
  from: AbstractDate,
  to: AbstractDate,
  timezone: Timezone = "UTC",
  nowDate?: AbstractDate,
  currentPeriodTo?: AbstractDate,
  limitTo100Days?: boolean,
  isHourTimeframeUpdateEnabled?: boolean
) {
  const periodTo =
    currentPeriodTo && limitTo100Days
      ? mmt(currentPeriodTo).toDate()
      : new Date();
  const maxFrom = limitTo100Days
    ? new Date(periodTo.setDate(periodTo.getDate() - 100))
    : from;
  const _from = getDateFromDateMath(maxFrom, timezone, false, nowDate);
  const _to = to
    ? getDateFromDateMath(to, timezone, true, nowDate)
    : endOf(now(timezone), Interval.Day);
  return {
    from: _from,
    to: _to,
    interval: getRangeInterval(_from, _to, isHourTimeframeUpdateEnabled),
  };
}

function getRangeInterval(
  from: AbstractDate,
  to: AbstractDate,
  isHourTimeframeUpdateEnabled?: boolean
) {
  const hourThreshold = isHourTimeframeUpdateEnabled ? 48 : 36;
  const dayThreshold = isHourTimeframeUpdateEnabled ? 3 : 2;

  const count = diff(from, to, Interval.Day, true);
  if (count <= dayThreshold) {
    return diff(from, to, Interval.Hour, true) <= hourThreshold
      ? Interval.Hour
      : Interval.Day;
  } else {
    return count <= 100 ? Interval.Day : Interval.Month;
  }
}

export function isDateMath(date: AbstractDate): date is string {
  if (!date || isMoment(date) || isDate(date) || isNumber(date)) {
    return false;
  } else {
    return date.indexOf("now") !== -1;
  }
}

function fromOperationsToDate(
  operations: any, // @TODO
  timezone: Timezone = "UTC",
  roundUp: boolean = false,
  nowDate?: AbstractDate
) {
  const time = nowDate ? tz(nowDate, timezone) : now(timezone);
  each(operations, ({ unit, type, num }) => {
    if (type === 0) {
      !roundUp
        ? time.startOf(unit === "w" ? "isoWeek" : unit === "q" ? "Q" : unit)
        : time
            .subtract(1, unit === "w" ? "isoWeek" : unit === "q" ? "Q" : unit)
            .endOf(unit === "w" ? "isoWeek" : unit === "q" ? "Q" : unit);
    } else if (type === 1) {
      time.add(num, unit === "w" ? "isoWeek" : unit === "q" ? "Q" : unit);
    } else if (type === 2) {
      time.subtract(num, unit === "w" ? "isoWeek" : unit === "q" ? "Q" : unit);
    }
  });
  return time;
}

function fromOperationsToDateMath(operations: DateMathOperation[]) {
  return reduce(
    operations,
    (dateMath, operation) => {
      if (operation.type === 2) {
        return (dateMath += "-" + operation.num + operation.unit);
      } else {
        return (dateMath += "/" + operation.unit);
      }
    },
    "now"
  );
}

/*
 * This is a simplified version of elasticsearch's date parser.
 * If you pass in a momentjs instance as the third parameter the calculation
 * will be done using this (and its locale settings) instead of the one bundled
 * with this library.
 */

export function extractOperations(date: string): DateMathOperation[] {
  const mathString = date.substring("now".length);
  const operations: DateMathOperation[] = [];
  const len = mathString.length;
  let i = 0;

  while (i < len) {
    const c = mathString.charAt(i++);
    let type;
    let num;
    let unit;

    if (c === "/") {
      type = 0;
    } else if (c === "+") {
      type = 1;
    } else if (c === "-") {
      type = 2;
    } else {
      // @ts-ignore
      return;
    }

    if (isNaN(parseInt(mathString.charAt(i)))) {
      num = 1;
    } else if (mathString.length === 2) {
      num = mathString.charAt(i);
    } else {
      const numFrom = i;
      while (!isNaN(parseInt(mathString.charAt(i)))) {
        i++;
        // @ts-ignore
        if (i >= len) return;
      }
      num = parseInt(mathString.substring(numFrom, i), 10);
    }

    if (type === 0) {
      // rounding is only allowed on whole, single, units (eg M or 1M, not 0.5M or 2M)
      if (num !== 1) {
        // @ts-ignore
        return;
      }
    }

    unit = mathString.charAt(i++);

    // append additional characters in the unit
    for (let j = i; j < len; j++) {
      const unitChar = mathString.charAt(i);
      if (/[a-z]/i.test(unitChar)) {
        unit += unitChar;
        i++;
      } else {
        break;
      }
    }

    operations.push({
      unit,
      type,
      num,
    } as DateMathOperation);
  }

  return operations;
}

export function setLocale(locale: Locale | "en") {
  return moment.locale(locale);
}

export function ageFromDate(
  date: string | AbstractDate,
  timezone: Timezone = "UTC"
): number {
  if (isDateMath(date)) {
    return ageFromDate(getDateFromDateMath(date, timezone), timezone);
  } else {
    return diff(date, now(timezone), Interval.Year, false);
  }
}

export function isFutureDate(date: AbstractDate): boolean {
  const check = (isoDate: string) => new Date() < new Date(isoDate);
  if (isMoment(date)) {
    return check(date.toISOString());
  }
  return check(date.toString());
}
